import type { ComputedRef } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'

import { useKidStore } from './store'

interface KidFieldsReturn {
  refName: ComputedRef<string>
  refGrade: ComputedRef<string | null>
  refVoip: ComputedRef<string | null>
  refNotes: ComputedRef<string>
  gradeOptions: ComputedRef<NativeSelectOption[]>
  voipOptions: ComputedRef<NativeSelectOption[]>
  setName: (value: string | number) => void
  setGrade: (option: NativeSelectOption) => void
  setVoip: (option: NativeSelectOption) => void
  setNotes: (event: AppTextareaEvent) => void
}

const toOptions = (source: Record<string, string> | undefined, selected: string | null): NativeSelectOption[] =>
  Object.entries(source ?? {}).map(([value, text]) => ({
    id: value,
    value,
    text,
    selected: value === selected,
  }))

export const useKidFields = (): KidFieldsReturn => {
  const kidStore = useKidStore()
  const listsStore = useListsStore()

  const refName = computed<string>(() => kidStore.getForm.name)
  const refGrade = computed<string | null>(() => kidStore.getForm.grade)
  const refVoip = computed<string | null>(() => kidStore.getForm.voip)
  const refNotes = computed<string>(() => kidStore.getForm.notes ?? '')

  const gradeOptions = computed<NativeSelectOption[]>(() => toOptions(listsStore.getLists?.kid.grade, refGrade.value))
  const voipOptions = computed<NativeSelectOption[]>(() => toOptions(listsStore.getLists?.kid.voip, refVoip.value))

  const setName = (value: string | number): void => {
    kidStore.setFormField('name', String(value))
  }

  const setGrade = (option: NativeSelectOption): void => {
    kidStore.setFormField('grade', option.value === null ? null : String(option.value))
  }

  const setVoip = (option: NativeSelectOption): void => {
    kidStore.setFormField('voip', option.value === null ? null : String(option.value))
  }

  const setNotes = (event: AppTextareaEvent): void => {
    kidStore.setFormField('notes', event.value)
  }

  return {
    refName,
    refGrade,
    refVoip,
    refNotes,
    gradeOptions,
    voipOptions,
    setName,
    setGrade,
    setVoip,
    setNotes,
  }
}
